/**
 * Turn synchronization for P2P multiplayer.
 *
 * - Team ownership (which players are local humans / remote humans)
 * - The ordered remote action queue: actions arrive with a sequence number and
 *   are handed to the game in order, when the game asks for them
 * - Per-turn checksum exchange for desync detection
 * - WASM capability handshake
 */

import type { GameState, TurnInput, Vec3 } from '../types';
import type { PeerManager } from './peer';
import type { NetMessage, NetAction, NetDeploySelection } from './protocol';
import { computeStateChecksum } from './protocol';

const CHECKSUM_HISTORY = 32;

/**
 * What the game needs from a source of another party's turns: the P2P peer
 * (NetworkSync) or a replay record (replay/ReplayDriver). The game treats
 * "remote humans" identically either way — their actions arrive on an ordered
 * queue and are applied through the same perform* paths.
 */
export interface TurnSync {
  readonly kind: 'p2p' | 'replay';
  localTeamIndex: number;
  remoteTeamIndex: number;
  remoteWasm: boolean | null;
  onActionQueued: (() => void) | null;
  onRemoteDeploySelection: ((msg: NetDeploySelection) => void) | null;
  onDesync: ((turnNumber: number, playerIndex: number, localHash: number, remoteHash: number) => void) | null;
  setTeamIndices(hostTeamIndex: number, clientTeamIndex: number): void;
  isRemoteTeam(teamIndex: number): boolean;
  isRemoteHuman(player: { teamIndex: number; isBot: boolean }): boolean;
  isLocalHuman(player: { teamIndex: number; isBot: boolean }): boolean;
  sendHello(wasm: boolean): void;
  sendAction(turnNumber: number, playerIndex: number, input: TurnInput): void;
  sendDeploySelection(teamIndex: number, planetIndex: number, surfaceDir: Vec3): void;
  sendChecksum(state: GameState): void;
  peekAction(): NetAction | null;
  takeAction(): NetAction | null;
  drainDeploySelections(): NetDeploySelection[];
}

/** What the game looks like to the remote-action gate (see canApplyNetAction). */
export interface NetActionGate {
  turnNumber: number;
  currentPlayerIndex: number;
  phase: GameState['phase'];
  players: readonly { id: number; teamIndex: number; isBot: boolean }[];
  /** Owner of the shot in the air, if any. */
  projectileOwnerId: number | null;
  /** The flight is precomputed and no bomblet launch is pending. */
  flightReady: boolean;
  /** Something is still animating (turn advance, walk, drone, airborne units, drop cans). */
  busy: boolean;
  isRemoteHuman(player: { teamIndex: number; isBot: boolean }): boolean;
}

/**
 * Whether the next remote action can be applied now. Actions wait on the queue
 * (never dropped) until the game is at the turn, unit and phase they were sent
 * from. Flight-sync markers ('detonate', 'flightEnd') belong to the shot in the
 * air, not to the unit whose turn it is: in a plan-then-resolve batch the
 * current unit is already the next team's, and the batch's later flights send
 * their markers while an earlier one may still be playing here — so a marker
 * applies only to its own shooter's flight.
 */
export function canApplyNetAction(msg: NetAction, g: NetActionGate): boolean {
  if (msg.turnNumber !== g.turnNumber) return false;
  if (msg.playerIndex !== g.currentPlayerIndex) return false;
  if (msg.input.action === 'detonate' || msg.input.action === 'flightEnd') {
    if (g.phase !== 'firing' || !g.flightReady || g.projectileOwnerId === null) return false;
    if (msg.input.playerId !== g.projectileOwnerId) return false;
    const owner = g.players.find(p => p.id === g.projectileOwnerId);
    return !!owner && g.isRemoteHuman(owner);
  }
  const player = g.players[msg.playerIndex];
  if (!player || !g.isRemoteHuman(player)) return false;
  if (g.phase !== 'aiming') return false;
  return !g.busy;
}

export class NetworkSync implements TurnSync {
  readonly kind = 'p2p' as const;
  private peer: PeerManager;

  /** Team indices controlled by this peer (host = [0], client = [1]) */
  localTeamIndex: number;
  remoteTeamIndex: number;

  // Ordered action stream
  private nextSendSeq = 1;
  private nextExpectedSeq = 1;
  private actionQueue: NetAction[] = [];   // sorted by seq
  private deployBuffer: NetDeploySelection[] = [];

  // Checksums keyed by `${turnNumber}:${playerIndex}`
  private localHashes: Map<string, number> = new Map();
  private remoteHashes: Map<string, number> = new Map();

  /** Remote peer's WASM capability (null until its hello arrives). */
  remoteWasm: boolean | null = null;

  // Callbacks — set by Game
  onActionQueued: (() => void) | null = null;
  onRemoteDeploySelection: ((msg: NetDeploySelection) => void) | null = null;
  onHello: ((wasm: boolean) => void) | null = null;
  onDesync: ((turnNumber: number, playerIndex: number, localHash: number, remoteHash: number) => void) | null = null;

  readonly isHost: boolean;

  constructor(peer: PeerManager, isHost: boolean) {
    this.peer = peer;
    this.isHost = isHost;
    this.localTeamIndex = isHost ? 0 : 1;
    this.remoteTeamIndex = isHost ? 1 : 0;
    peer.onMessage = (msg) => this.handleMessage(msg);
  }

  /** Update team indices to match actual player team assignments. */
  setTeamIndices(hostTeamIndex: number, clientTeamIndex: number): void {
    this.localTeamIndex = this.isHost ? hostTeamIndex : clientTeamIndex;
    this.remoteTeamIndex = this.isHost ? clientTeamIndex : hostTeamIndex;
  }

  isLocalTeam(teamIndex: number): boolean { return teamIndex === this.localTeamIndex; }
  isRemoteTeam(teamIndex: number): boolean { return teamIndex === this.remoteTeamIndex; }
  isRemoteHuman(player: { teamIndex: number; isBot: boolean }): boolean {
    return player.teamIndex === this.remoteTeamIndex && !player.isBot;
  }
  isLocalHuman(player: { teamIndex: number; isBot: boolean }): boolean {
    return player.teamIndex === this.localTeamIndex && !player.isBot;
  }

  // --- Sending ---

  sendHello(wasm: boolean): void {
    this.peer.send({ type: 'hello', wasm });
  }

  sendAction(turnNumber: number, playerIndex: number, input: TurnInput): void {
    this.peer.send({ type: 'action', seq: this.nextSendSeq++, turnNumber, playerIndex, input });
  }

  sendDeploySelection(teamIndex: number, planetIndex: number, surfaceDir: Vec3): void {
    this.peer.send({ type: 'deploySelection', teamIndex, planetIndex, surfaceDir });
  }

  /** Send this turn's checksum and compare with the remote's if it already arrived. */
  sendChecksum(state: GameState): void {
    const key = `${state.turnNumber}:${state.currentPlayerIndex}`;
    const hash = computeStateChecksum(state);
    this.peer.send({ type: 'checksum', turnNumber: state.turnNumber, playerIndex: state.currentPlayerIndex, hash });
    this.localHashes.set(key, hash);
    this.compare(key, state.turnNumber, state.currentPlayerIndex);
    this.prune(this.localHashes);
  }

  private compare(key: string, turnNumber: number, playerIndex: number): void {
    const local = this.localHashes.get(key);
    const remote = this.remoteHashes.get(key);
    if (local === undefined || remote === undefined) return;
    this.localHashes.delete(key);
    this.remoteHashes.delete(key);
    if (local !== remote) this.onDesync?.(turnNumber, playerIndex, local, remote);
  }

  private prune(map: Map<string, number>): void {
    while (map.size > CHECKSUM_HISTORY) {
      const first = map.keys().next().value as string;
      map.delete(first);
    }
  }

  // --- Receiving ---

  private handleMessage(msg: NetMessage): void {
    switch (msg.type) {
      case 'hello':
        this.remoteWasm = msg.wasm;
        this.onHello?.(msg.wasm);
        break;
      case 'action': {
        // Insert by seq (reliable channel → almost always append)
        let i = this.actionQueue.length;
        while (i > 0 && this.actionQueue[i - 1].seq > msg.seq) i--;
        if (!this.actionQueue.some(a => a.seq === msg.seq)) this.actionQueue.splice(i, 0, msg);
        this.onActionQueued?.();
        break;
      }
      case 'deploySelection':
        if (this.onRemoteDeploySelection) this.onRemoteDeploySelection(msg);
        else this.deployBuffer.push(msg);
        break;
      case 'checksum': {
        const key = `${msg.turnNumber}:${msg.playerIndex}`;
        this.remoteHashes.set(key, msg.hash);
        this.compare(key, msg.turnNumber, msg.playerIndex);
        this.prune(this.remoteHashes);
        break;
      }
      default:
        break;
    }
  }

  // --- Action queue ---

  /** The next in-order remote action, or null if none (or a gap in sequence). */
  peekAction(): NetAction | null {
    const head = this.actionQueue[0];
    if (!head) return null;
    return head.seq === this.nextExpectedSeq ? head : null;
  }

  /** Remove and return the head action (caller has applied it). */
  takeAction(): NetAction | null {
    const head = this.peekAction();
    if (!head) return null;
    this.actionQueue.shift();
    this.nextExpectedSeq = head.seq + 1;
    return head;
  }

  pendingActionCount(): number { return this.actionQueue.length; }

  // --- Deploy ---

  drainDeploySelections(): NetDeploySelection[] {
    const buf = [...this.deployBuffer];
    this.deployBuffer = [];
    return buf;
  }
}
